"use client";

import { Play, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";

type ClickToPlayDemoVideoProps = {
  src: string;
  ariaLabel: string;
  variant?: "thumb" | "featured";
  className?: string;
};

export function ClickToPlayDemoVideo({
  src,
  ariaLabel,
  variant = "featured",
  className = "",
}: ClickToPlayDemoVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const startPlayback = async () => {
    const video = videoRef.current;
    if (!video) return false;
    video.muted = false;
    try {
      await video.play();
      setIsPlaying(true);
      return true;
    } catch {
      setIsPlaying(false);
      return false;
    }
  };

  const handleExpandAndPlay = () => {
    flushSync(() => setIsExpanded(true));
    void startPlayback();
  };

  const handlePlay = () => {
    void startPlayback();
  };

  const handleCollapse = () => {
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
      video.muted = true;
    }
    setIsPlaying(false);
    setIsExpanded(false);
  };

  const handlePause = () => {
    if (variant === "thumb" && isExpanded) return;
    setIsPlaying(false);
  };

  const handleEnded = () => {
    if (variant === "thumb") {
      handleCollapse();
      return;
    }
    setIsPlaying(false);
  };

  useEffect(() => {
    if (!isExpanded) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") handleCollapse();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isExpanded]);

  const playOverlay = (
    <button
      type="button"
      onClick={() => (variant === "thumb" ? handleExpandAndPlay() : handlePlay())}
      className="absolute inset-0 z-10 flex items-center justify-center bg-black/35 transition hover:bg-black/45"
      aria-label={`Play ${ariaLabel}`}
    >
      <span
        className={`flex items-center justify-center rounded-full bg-white/95 text-gray-950 shadow-lg ${
          variant === "thumb" ? "h-9 w-9" : "h-10 w-10"
        }`}
      >
        <Play
          className={`fill-current pl-0.5 ${variant === "thumb" ? "h-4 w-4" : "h-5 w-5"}`}
          aria-hidden
        />
      </span>
    </button>
  );

  if (variant === "thumb") {
    return (
      <div
        className={
          isExpanded
            ? "fixed inset-0 z-[200] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
            : `relative shrink-0 ${className}`
        }
        role={isExpanded ? "dialog" : undefined}
        aria-modal={isExpanded ? true : undefined}
        aria-label={isExpanded ? ariaLabel : undefined}
        onClick={isExpanded ? handleCollapse : undefined}
      >
        <div
          className={
            isExpanded
              ? "relative w-full max-w-[300px]"
              : `relative h-full w-full overflow-hidden rounded-xl bg-black shadow-md ring-1 ring-gray-200/80`
          }
          onClick={isExpanded ? (event) => event.stopPropagation() : undefined}
        >
          {isExpanded && (
            <button
              type="button"
              onClick={handleCollapse}
              className="absolute -right-2 -top-2 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-white text-gray-950 shadow-lg transition hover:bg-gray-100"
              aria-label="Close video"
            >
              <X className="h-5 w-5" aria-hidden />
            </button>
          )}
          <div
            className={
              isExpanded
                ? "aspect-[9/16] w-full overflow-hidden rounded-[1.75rem] border border-white/20 bg-black shadow-2xl"
                : "aspect-[9/16] h-full w-full"
            }
          >
            <video
              ref={videoRef}
              src={src}
              preload="metadata"
              playsInline
              muted={!isPlaying}
              controls={isPlaying}
              onPlay={() => setIsPlaying(true)}
              onPause={handlePause}
              onEnded={handleEnded}
              className="h-full w-full object-cover"
              aria-label={ariaLabel}
            />
            {!isPlaying && playOverlay}
          </div>
          {isExpanded && (
            <p className="mt-2 text-center text-xs font-bold text-white/80">
              Wine upsell role-play
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`relative bg-black ${className}`}>
      <video
        ref={videoRef}
        src={src}
        preload="metadata"
        playsInline
        muted={!isPlaying}
        controls={isPlaying}
        loop={isPlaying}
        onPlay={() => setIsPlaying(true)}
        onPause={handlePause}
        onEnded={handleEnded}
        className="h-full w-full object-cover"
        aria-label={ariaLabel}
      />
      {!isPlaying && playOverlay}
    </div>
  );
}
